import React from 'react';
import { Box, Stack, Typography, IconButton, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getFileIcon } from '../../../../common/utils';
import CustomTooltip from '../../../../common/components/CustomToolTip/CustomToolTip';
import { UploadedFile } from '../../types';

interface FileUploadSectionProps {
  uploadedFiles: UploadedFile[];
  onFileRemove: (id: string) => void;
  onFilePreview: (url: string) => void;
  isSubmitting: boolean;
}

const FileUploadSection: React.FC<FileUploadSectionProps> = ({
  uploadedFiles,
  onFileRemove,
  onFilePreview,
  isSubmitting,
}) => {
  if (uploadedFiles.length === 0) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" flexWrap="wrap" gap={1}>
        {uploadedFiles.map((file) => (
          <Box
            key={file.id}
            sx={{
              display: 'flex',
              alignItems: 'start',
              border: file.failed ? '1px solid #D32F2F' : '1px solid #E0E0E0',
              borderRadius: '4px',
              py: 0.5,
              px: 1,
              minWidth: 180,
              maxWidth: '100%',
              opacity: file.isUploading || file.isAnalyzing ? 0.6 : 1,
              cursor: 'pointer',
            }}
            onClick={() => file.url && onFilePreview(file.url)}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '6px',
                bgcolor: '#f1f3f4',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                mr: 1,
              }}
            >
              {file.isUploading || file.isAnalyzing ? (
                <CircularProgress size={14} sx={{ color: '#5f6368' }} />
              ) : file.failed ? (
                <CustomTooltip title={file?.error || ''} variant="bottom" offset={[0, 0]}>
                  <img src="/images/Error.svg" alt="error" style={{ width: 16, height: 16 }} />
                </CustomTooltip>
              ) : (
                getFileIcon(file.name, file.file)
              )}
            </Box>

            <Typography
              variant="body2"
              sx={{
                flex: 1,
                fontWeight: 500,
                fontSize: '14px',
                color: '#202124',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                mt: '4px',
                alignItems: 'start',
              }}
            >
              {file.name}
            </Typography>

            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onFileRemove(file.id);
              }}
              disabled={file.isUploading || file.isAnalyzing || isSubmitting}
              sx={{
                ml: 0.5,
                color: '#5f6368',
                '&:hover': { bgcolor: 'transparent', color: '#000' },
                '&:disabled': { color: '#ccc' },
              }}
            >
              <CloseIcon sx={{ height: 20, width: 20 }} />
            </IconButton>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default FileUploadSection;
